import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Check, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import Marca from "@/components/Marca";
import { Button, Card, ErrorBanner, Input, Spinner } from "@/components/ui";
import { API_BASE, tokens } from "@/lib/api";
import { idiomaDoNavegador, traduzir, useT } from "@/lib/i18n";

/** O plano escolhido na landing, só para confirmar a escolha.
 *
 *  **Sem preço.** Ele saiu daqui na Fase 2: o valor passou a ser por país
 *  (`PlanPrice`), e uma tabela cravada em reais mostraria "R$ 79" para
 *  quem acabou de ver "$46" na landing. O número que vale está lá e na
 *  tela de assinatura; repeti-lo aqui só cria uma terceira verdade. */
const PLANOS: Record<string, { nome: Record<string, string>; limite: number }> = {
  essencial: { nome: { BR: "Essencial", US: "Essential" }, limite: 3 },
  profissional: { nome: { BR: "Profissional", US: "Professional" }, limite: 8 },
  studio: { nome: { BR: "Studio", US: "Studio" }, limite: 15 },
};

/** O nome do plano na língua em que a landing acabou de mostrá-lo.
 *
 *  Por país e não por idioma porque é assim que o dado existe: o nome
 *  mora em `PlanPrice`, que é por país, e é ele que a landing renderiza
 *  (`plan.resolved_price.display_name`). Casar pelo idioma faria a landing
 *  dizer "Professional" e esta tela responder "Profissional" para quem
 *  está nos EUA lendo em português.
 *
 *  ⚠ Cópia deliberada, e limitada a este eco da escolha do visitante: a
 *  tela é anterior ao login e `/billing/plans/` exige sessão. Quem manda
 *  no nome, no preço e no limite continua sendo o banco — nada aqui é
 *  usado para cobrar. */
const nomeDoPlano = (code: string, country: string) => {
  const plano = PLANOS[code] ?? PLANOS.profissional;
  return plano.nome[country] ?? plano.nome.BR;
};

type SlugStatus = {
  slug: string;
  available: boolean;
  reason: string | null;
  url?: string;
};

function mensagemDeErro(error: unknown): string {
  const data = (error as { response?: { data?: unknown } }).response?.data;
  if (!data || typeof data !== "object") {
    return traduzir(idiomaDoNavegador(), "signup.failed");
  }

  const envelope = (data as { error?: { message?: string } }).error;
  if (envelope?.message) return envelope.message;

  const primeiro = Object.values(data as Record<string, unknown>)[0];
  if (Array.isArray(primeiro) && typeof primeiro[0] === "string") return primeiro[0];
  if (typeof primeiro === "string") return primeiro;
  return traduzir(idiomaDoNavegador(), "signup.failed");
}

export default function Signup() {
  const t = useT();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const planoCode = params.get("plano") ?? "profissional";
  const pais = params.get("country") ?? "BR";
  const plano = PLANOS[planoCode] ?? PLANOS.profissional;

  const [form, setForm] = useState({
    owner_name: "",
    email: "",
    password: "",
    garage_name: "",
    phone: "",
  });
  const [slug, setSlug] = useState("");
  const [slugTocado, setSlugTocado] = useState(false);
  const [slugStatus, setSlugStatus] = useState<SlugStatus | null>(null);

  const set = (campo: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [campo]: e.target.value });

  // Sugere o endereço a partir do nome, até o dono editar manualmente.
  const slugEfetivo = slugTocado ? slug : form.garage_name;

  useEffect(() => {
    if (slugEfetivo.trim().length < 3) {
      setSlugStatus(null);
      return;
    }
    // debounce: sem isto é uma requisição por tecla digitada
    const t = setTimeout(() => {
      axios
        .get(`${API_BASE}/signup/slug/`, { params: { slug: slugEfetivo } })
        .then((r) => setSlugStatus(r.data))
        .catch(() => setSlugStatus(null));
    }, 350);
    return () => clearTimeout(t);
  }, [slugEfetivo]);

  const signup = useMutation({
    mutationFn: async () => {
      const { data } = await axios.post(`${API_BASE}/signup/`, {
        ...form,
        slug: slugStatus?.available ? slugStatus.slug : "",
        plan: planoCode,
        // O país vem do link da landing, já detectado pelo IP. Sem ele a
        // oficina nasce brasileira — moeda, placa, catálogo, fuso e
        // documento todos errados para quem está em Miami.
        //
        // O backend valida contra as regiões atendidas e ignora lixo: o
        // parâmetro é de URL, e recusar o cadastro por um caractere
        // editado seria perder o cliente na última tela.
        country: params.get("country") ?? "",
      });
      return data;
    },
    onSuccess: (data) => {
      qc.clear();
      tokens.set(data);
      // Vai direto para o assistente: oficina criada ainda não recebe reserva.
      navigate("/comecar");
    },
  });

  // A API responde em dois formatos: erro de negócio no envelope
  // { error: { code, message } } — tratado por toApiError — e erro de
  // validação de campo { campo: ["mensagem"] }. Um String(obj) ingênuo
  // aqui vira "[object Object]" na cara do usuário.
  const erro = signup.isError ? mensagemDeErro(signup.error) : undefined;

  const podeEnviar =
    form.owner_name.trim().length > 2 &&
    form.email.includes("@") &&
    form.password.length >= 10 &&
    form.garage_name.trim().length > 2 &&
    slugStatus?.available === true;

  return (
    <div className="min-h-dvh px-4 py-10">
      <div className="mx-auto max-w-md">
        <div className="mb-8">
          <Marca />
          <p className="mt-2 text-xs text-steel-dim">{t("signup.subtitle")}</p>
        </div>

        <Card className="mb-4 border-racing-600/40 bg-racing-500/5 p-4">
          <p className="eyebrow mb-1">{t("signup.chosenPlan")}</p>
          <p className="font-medium">
            {nomeDoPlano(planoCode, pais)}
            <span className="text-steel-dim">
              {" — "}
              {t("signup.upTo", { n: plano.limite })}
            </span>
          </p>
          <p className="mt-1 text-sm text-steel-muted">{t("signup.trialNote")}</p>
          <p className="mt-1 text-xs text-racing-400">{t("signup.earlyAccess")}</p>
        </Card>

        <Card className="p-6">
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              signup.mutate();
            }}
          >
            <Input
              label={t("signup.shopName")}
              required
              autoFocus
              value={form.garage_name}
              onChange={set("garage_name")}
              placeholder={t("signup.shopNamePlaceholder")}
            />

            <div>
              <span className="eyebrow mb-1.5 block text-steel-dim">
                {t("signup.pageAddress")}
              </span>
              <div className="flex items-center gap-1 rounded-[4px] border border-line bg-carbon-900 px-3 focus-within:border-nitro-500">
                <input
                  value={slugEfetivo}
                  onChange={(e) => {
                    setSlugTocado(true);
                    setSlug(e.target.value);
                  }}
                  className="min-w-0 flex-1 bg-transparent py-2.5 text-steel outline-none"
                  placeholder={t("signup.slugPlaceholder")}
                />
                <span className="shrink-0 text-sm text-steel-dim">.garagem.club</span>
              </div>
              {slugStatus && (
                <p
                  className={`mt-1.5 flex items-center gap-1.5 text-sm ${
                    slugStatus.available ? "text-emerald-400" : "text-red-400"
                  }`}
                >
                  {slugStatus.available ? <Check size={14} /> : <X size={14} />}
                  {slugStatus.available
                    ? t("signup.slugFree", { url: slugStatus.url ?? "" })
                    : slugStatus.reason}
                </p>
              )}
            </div>

            <hr className="border-line" />

            <Input
              label={t("signup.yourName")}
              required
              value={form.owner_name}
              onChange={set("owner_name")}
              autoComplete="name"
            />
            <Input
              label={t("common.email")}
              type="email"
              required
              value={form.email}
              onChange={set("email")}
              autoComplete="email"
            />
            {/* Canal por país: WhatsApp no Brasil, SMS nos EUA — pedir
                WhatsApp a uma oficina do Texas faz o dono achar que o
                produto não é para ele. Ver Region.messaging_channel. */}
            <Input
              label={pais === "US" ? t("common.phone") : "WhatsApp"}
              type="tel"
              inputMode="tel"
              value={form.phone}
              onChange={set("phone")}
              autoComplete="tel"
              placeholder={
                pais === "US" ? "(305) 555-0199" : "(11) 99999-9999"
              }
            />
            <Input
              label={t("auth.password")}
              type="password"
              required
              value={form.password}
              onChange={set("password")}
              autoComplete="new-password"
              hint={t("signup.passwordHint")}
            />

            <ErrorBanner message={erro} />

            <Button
              type="submit"
              variant="racing"
              size="lg"
              className="w-full"
              disabled={!podeEnviar || signup.isPending}
            >
              {signup.isPending ? (
                <Spinner className="size-4 border-carbon-900/30 border-t-carbon-900" />
              ) : (
                t("signup.submit")
              )}
            </Button>
          </form>
        </Card>

        <p className="mt-5 text-center text-sm text-steel-dim">
          {t("signup.haveAccount")}{" "}
          <a href="/entrar" className="text-racing-500 hover:underline">
            {t("team.signIn")}
          </a>
        </p>
      </div>
    </div>
  );
}
